<template>
  <a-form class="login-form" ref="ssoFormRef" :model="ssoFormState" v-bind="layout" @finish="handleFinishSSO" @finishFailed="handleFinishFailedSSO" v-if="isAddin">
    <a-form-item has-feedback :label="t('changepassword.login')" :rules="[{ required: true, message: t('validations.required') }]" name="email" required>
      <a-input v-model:value="ssoFormState.email" autocomplete="off" :disabled="isSSOLoading" />
    </a-form-item>
    <a-form-item :wrapper-col="{ span: 16, offset: 8 }">
      <a-row>
        <a-button type="primary" html-type="submit" :loading="isSSOLoading">{{ t("login.action.login") }}</a-button>
      </a-row>
    </a-form-item>
  </a-form>
  <a-tabs v-model:activeKey="activeTab" type="card" v-else-if="showResetForm === false">
    <a-tab-pane key="jaas" tab="Benutzername + Passwort">
      <a-form name="custom-validation" class="login-form" ref="formRef" :model="formState" :rules="rules" v-bind="layout" @finish="handleFinish" @finishFailed="handleFinishFailed">
        <a-form-item has-feedback :label="t('changepassword.login')" name="email" required>
          <a-input v-model:value="formState.email" autocomplete="off" :disabled="isLoading" />
        </a-form-item>
        <a-form-item has-feedback :extra="passwordField.message" :validateStatus="passwordField.status" :label="t('changepassword.password')" name="password" required>
          <a-input-password v-model:value="formState.password" type="password" autocomplete="off" :disabled="isLoading" />
        </a-form-item>

        <a-form-item :wrapper-col="{ span: 16, offset: 8 }">
          <a-row>
            <a-button type="primary" html-type="submit" :loading="isLoading">{{ t("login.action.login") }}</a-button>
            <a-button @click="showResetForm = true" :disabled="isLoading">{{ t("login.passwordreset.forgot") }}</a-button>
          </a-row>
        </a-form-item>
      </a-form>
    </a-tab-pane>
    <a-tab-pane key="sso" tab="Single-Sign-On (SSO)">
      <a-form class="login-form" ref="ssoFormRef" :model="ssoFormState" v-bind="layout" @finish="handleFinishSSO" @finishFailed="handleFinishFailedSSO">
        <a-form-item has-feedback :label="t('changepassword.login')" :rules="[{ required: true, message: t('validations.required') }]" name="email" required>
          <a-input v-model:value="ssoFormState.email" autocomplete="off" :disabled="isSSOLoading" />
        </a-form-item>
        <a-form-item :wrapper-col="{ span: 16, offset: 8 }">
          <a-row>
            <a-button type="primary" html-type="submit" :loading="isSSOLoading">{{ t("login.action.login") }}</a-button>
          </a-row>
        </a-form-item>
      </a-form>
    </a-tab-pane>
    <a-tab-pane key="id-astria" tab="ID-Austria" disabledz> </a-tab-pane>
  </a-tabs>
  <div v-else>
    <a-form v-if="result === 'none' || result === 'loading'" :model="formState" :rules="rules" v-bind="layout" @finish="resetPass" @finishFailed="handleFinishFailed">
      <a-form-item required has-feedback :label="t('passwordreset.email')" name="email">
        <a-input v-model:value="formState.email" autocomplete="off" :disabled="result === 'loading'" />
      </a-form-item>

      <a-form-item :wrapper-col="{ span: 16, offset: 8 }">
        <a-row>
          <a-button @click="showResetForm = false" :disabled="result === 'loading'">
            <template #icon>
              <LeftOutlined />
            </template>
            {{ t("dialog.button.back") }}
          </a-button>
          <a-button type="primary" :loading="result === 'loading'" html-type="submit">{{ t("common.action.send") }}</a-button>
        </a-row>
      </a-form-item>
    </a-form>
    <div v-else>
      <a-result :status="result" :title="resultTitle" :sub-title="resultSubtitle"></a-result>
    </div>
  </div>
</template>
<script lang="ts">
import { RuleObject, ValidateErrorEntity } from "ant-design-vue/es/form/interface";
import { LeftOutlined } from "@ant-design/icons-vue";
import { computed, defineComponent, nextTick, onMounted, reactive, ref, UnwrapRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { loginSSO, resetPassword } from "../api/user";
import router from "../router";
import { useUserStore } from "../store/modules/user";
import { deleteCookie, getCookie } from "../utils/cookies";
import { loadOfficeJs } from "../utils/loadOffice";
import { message } from "ant-design-vue";

interface FormState {
  password: string;
  email: string;
}
interface ResetFormState {
  email: string;
}
type ResetFormStatus = "success" | "error" | "loading" | "none";

export default defineComponent({
  components: { LeftOutlined },
  emits: ["on-failure", "on-success"],
  setup(props, { emit }) {
    const store = useUserStore();
    const { t } = useI18n();
    const formRef = ref();
    const showResetForm = ref(false);
    const isLoading = ref(false);
    const isSSOLoading = ref(false);
    const oauthHandled = ref(false);
    const passwordField = reactive({
      status: "",
      message: "",
    });
    const resultTitle = ref("");
    const resultSubtitle = ref("");
    const formState: UnwrapRef<FormState> = reactive({
      email: "",
      password: "",
    });
    const resetFormState: UnwrapRef<ResetFormState> = reactive({
      email: "",
    });

    let validateEmail = async (rule: RuleObject, value: string) => {
      if (value === "") {
        return Promise.reject(t("validations.required"));
      } else {
        return Promise.resolve();
      }
    };
    let validatePassword = async (rule: RuleObject, value: string) => {
      if (showResetForm.value === true) {
        return Promise.resolve();
      }
      if (value === "") {
        return Promise.reject(t("validations.required"));
      }
      isLoading.value = true;
      try {
        await store.authRequest({
          username: formState.email,
          password: formState.password,
        });
      } catch {
        // authRequest is expected to swallow its own errors, but guard anyway
      } finally {
        isLoading.value = false;
      }
      if (store.authStatus !== "success") {
        showCredentialsError();
        return Promise.reject(t("login.invalidcredentials"));
      }
      nextTick(() => {
        router.push("/home");
      });
      return Promise.resolve();
    };
    const authStatus = computed(() => {
      return store.authStatus;
    });

    watch(
      () => authStatus,
      () => {
        if (authStatus.value === "error") {
          isLoading.value = false;
          showCredentialsError();
        }
      }
    );

    const result = ref<ResetFormStatus>("none");
    const isAddin = ref(false);
    const resetPass = (values: ResetFormState) => {
      result.value = "loading";

      resetPassword(formState.email)
        .then(() => {
          result.value = "success";
          resultTitle.value = t("resetpassword.sent");
          resultSubtitle.value = t("resetpassword.success");

          setTimeout(() => {
            showResetForm.value = false;
            result.value = "none";
          }, 6000);
        })
        .catch((error) => {
          result.value = "error";
          resultTitle.value = t("contactpage.send.error.title");
          resultSubtitle.value = t("contactpage.send.error.subTitle");
        });
    };
    const rules = {
      password: [{ validator: validatePassword, trigger: "submit" }],
      email: [
        { validator: validateEmail, trigger: "submit" },
        { required: true, message: "Bitte geben Sie Ihren Benutzernamen ein" },
      ],
    };
    const layout = {
      labelCol: { span: 8 },
      wrapperCol: { span: 14 },
    };
    const handleFinish = (values: FormState) => {
      store.resetLogoutFlag();
      emit("on-success");
    };
    const handleFinishFailed = (errors: ValidateErrorEntity<FormState>) => {
      store.resetLogoutFlag();
      isLoading.value = false;
      console.log("Unable to login");
    };

    const showCredentialsError = () => {
      passwordField.message = t("login.invalidcredentials");
      passwordField.status = "error";
    };
    if (store.authStatus === "error") {
      showCredentialsError();
    }

    const ssoFormState = reactive<{ email: string }>({
      email: "",
    });
    const handleFinishSSO = async () => {
      const currentUrl = window.location.href;
      const url = new URL(currentUrl);
      const params = new URLSearchParams(url.search);
      const app = params.get("app") || "";
      isSSOLoading.value = true;
      try {
        const result = await loginSSO(ssoFormState.email, app);
        window.location.href = result;
      } catch (error) {
        isSSOLoading.value = false;
        console.log("SSO FAILED");
        message.error(error as string);
      }
    };
    const handleFinishFailedSSO = () => {
      isSSOLoading.value = false;
      console.log("SSO FAILED");
    };

    onMounted(async () => {
      if (store.hasLoggedOut || oauthHandled.value) {
        return;
      }

      const token = getCookie("access_token");
      const refresh = getCookie("refresh_token");
      const currentUrl = window.location.href;
      const url = new URL(currentUrl);
      const params = new URLSearchParams(url.search);
      const email = params.get("email");
      const app = params.get("app") || "";

      if (email) {
        console.log("email provided, starting SSO login");
        isSSOLoading.value = true;
        try {
          const result = await loginSSO(email, app);
          window.location.href = result;
        } catch (error) {
          isSSOLoading.value = false;
          console.log("SSO with pre-given email failed");
          return;
        }
      }

      if (token && refresh) {
        oauthHandled.value = true;
        const isExpired = store.checkIfTokensAreExpired(refresh);
        if (isExpired) {
          console.log("OAuth tokens expired, clearing cookies");
          deleteCookie("access_token");
          deleteCookie("refresh_token");
          return;
        }

        if (app === "addin") {
          await loadOfficeJs();
          try {
            window.Office.onReady((info) => {
              console.log("Office Addon ready", info);
              console.log("Context after ready", window.Office.context);
              window.Office.context.ui.messageParent(
                JSON.stringify({
                  token: token,
                  refresh_token: refresh,
                }),
                { targetOrigin: "*" }
              );
              deleteCookie("access_token");
              deleteCookie("refresh_token");
              localStorage.removeItem("user-token");
              localStorage.removeItem("is-oauth-token");
            });
          } catch (e: any) {
            console.error("Error using office API", e);
          }
          return;
        }
        console.log("OAuth callback detected, handling tokens...");
        const success = await store.handleOAuthCallback();

        if (success) {
          router.push("/home");
        } else {
          console.error("Failed to handle OAuth callback");
          oauthHandled.value = false;
        }
      }
    });

    const currentUrl = window.location.href;
    const url = new URL(currentUrl);
    const params = new URLSearchParams(url.search);
    const login = params.get("login") || "jaas";
    if (login === "sso") {
      isAddin.value = true;
    }

    const activeTab = ref(login);
    return {
      activeTab,
      handleFinishSSO,
      handleFinishFailedSSO,
      ssoFormState,
      formState,
      formRef,
      rules,
      layout,
      handleFinishFailed,
      handleFinish,
      t,
      showResetForm,
      resetFormState,
      resetPass,
      result,
      resultTitle,
      resultSubtitle,
      isAddin,
      passwordField,
      isLoading,
      isSSOLoading,
    };
  },
});
</script>
<style>
.login-form .ant-form-extra {
  color: #ed6f6f;
}
</style>
